import { createContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { agregarEnMasa, agregarProducto, cambiarPrecioEnMasa, deleteProducto, getById, modificarProducto, obtenerProductos, obtenerProductosPorIds, publicarEnMasa, submitImagen } from "../services/productServices.js";
import { getTimestampWithOffsetFromDate } from "../extras/utils.js";
import debounce from 'lodash/debounce';

export const ProductContext = createContext();

const DEBOUNCE_DELAY = 600;

function ProductContextProvider({ children }) {
  const [productos, setProductos] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  const [filterState, setFilterState] = useState({
    userSearch: "",
    marca: "",
    estado: "",
    text: "",
    dateFrom: "",
    dateTo: "",
    codigo: "",
    UserId: "",
    temporada: "",
    display: 10,
    page: 1,
    omitProductSelection: false
  });

  const updateFilterState = useCallback((field, value) => {
    setFilterState(prev => ({ ...prev, [field]: value }));
  }, []);

  const debouncedGetProductos = useCallback(
    debounce(async (params) => {
      try {
        setIsLoadingProducts(true);
        setError(null);
        const result = await obtenerProductos(params);
        const { productos, totalRows } = result;
        setTotalRows(totalRows);
        setProductos(productos);
      } catch (err) {
        setError(err.mensaje);
        setProductos([]);
        setTotalRows(0);
      } finally {
        setIsLoadingProducts(false);
      }
    }, DEBOUNCE_DELAY),
    []
  );

  async function getProductosConQuery() {
    setLoading(true);
    try {
      const params = {
        ...filterState,
        codigo: filterState.omitProductSelection ? "" : filterState.codigo,
        UserId: filterState.omitProductSelection ? "" : filterState.UserId,
        dateFrom: getTimestampWithOffsetFromDate(filterState.dateFrom),
        dateTo: filterState.dateTo
          ? getTimestampWithOffsetFromDate(filterState.dateTo) + (1000 * 60 * 60 * 24)
          : null
      };
      await debouncedGetProductos(params);
    } catch (err) {
      setError(err.mensaje);
    } finally {
      setLoading(false);
    }
  }

  // Reset pagination and filters
  const resetFilters = useCallback(() => {
    setFilterState({
      userSearch: "",
      marca: "",
      estado: "CUALQUIERA",
      text: "",
      dateFrom: "",
      dateTo: "",
      codigo: "",
      UserId: "",
      temporada: "",
      display: 10,
      page: 1,
      omitProductSelection: false
    });
    setError(null);
  }, []);

  async function getProductosByIds(ids) {
    return obtenerProductosPorIds(ids);
  }

  async function obtenerProductoPorId(_id) {
    const producto = await getById(_id);
    return producto;
  }

  async function agregarUnProducto(producto) {
    const _id = await agregarProducto(producto);
    if (_id) getProductosConQuery();
    return _id;
  }
  async function modificarUnProducto(producto) {
    const status = await modificarProducto(producto);
    if (status === 200) getProductosConQuery();
    return status;
  }
  async function borrarUnProducto(_id) {
    const exito = await deleteProducto(_id);
    if (exito) getProductosConQuery();
    return exito;
  }

  async function agregarProductosEnMasa(aceptados, rechazados, mail) {
    const exito = await agregarEnMasa(aceptados, rechazados, mail);
    if (exito) getProductosConQuery();
    return exito;
  }

  async function PrecioEnMasa(ids, opType, porcentaje) {
    const exito = await cambiarPrecioEnMasa(ids, opType, porcentaje);
    if (exito) getProductosConQuery();
    return exito;
  }

  async function publicacionMasiva(idArray) {

    const exito = await publicarEnMasa(idArray);
    if (exito) getProductosConQuery();

    return exito;
  }

  async function modificarImagen(formData) {
    const exito = await submitImagen(formData);
    if (exito) getProductosConQuery();
    return exito;
  }

  useEffect(() => {
    if(filterState.omitProductSelection) return;
    getProductosConQuery();
    return () => {
      debouncedGetProductos.cancel();
    };
  }, [JSON.stringify(filterState)]);

  return (
    <ProductContext.Provider value={{
      loading,
      filterState,
      productos,
      totalRows,
      isLoadingProducts,
      error,
      updateFilterState,
      setFilterState,
      resetFilters,
      agregarUnProducto,
      modificarUnProducto,
      borrarUnProducto,
      agregarProductosEnMasa,
      PrecioEnMasa,
      modificarImagen,
      obtenerProductoPorId,
      publicacionMasiva,
      getProductosConQuery,
      getProductosByIds,
    }}>
      {children}
    </ProductContext.Provider>
  );
}

ProductContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ProductContextProvider;